import { useMount } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import type { Props } from './types.js';
import { useConfig } from '#~/config/hooks.js';
import { PageviewTracker } from './pageview-tracker.jsx';
import { ScreenTracker } from './screen-tracker.jsx';

/**
 * Third-party tag loaders (Google Tag Manager and such).
 *
 * These stay Production-only: injecting external tracking scripts into
 * dev/itg/staging would pollute those environments and can leak non-prod
 * traffic into production tag configs. Currently a placeholder mount point.
 */
const ThirdPartyTags = (): null => {
  useMount(() => {
    // @fixme - Google tag manager and such will go here.
  });

  return null;
};

/**
 * Tracking wrapper.
 *
 * `PageviewTracker` (the Iris route $pageview owner) mounts in EVERY
 * environment. posthog-js automatic pageview capture is disabled
 * unconditionally in `PostHogManager.initializeSync`, so this tracker is the
 * ONLY $pageview producer everywhere.
 *
 * The previous Production gate read `isProduction`, which derives from Vite's
 * build MODE — `production` for any `vite build`. It therefore excluded local
 * `vite dev` only; ITG, Staging and prod builds all already emitted route
 * pageviews. Mounting unconditionally makes local dev match them, so a
 * pageview change can be exercised while developing rather than only after a
 * build. Only the third-party tag loaders remain Production-gated, because
 * those inject external scripts.
 *
 * Every tracker mounted here owns its own mount condition. Both trackers are
 * childless siblings rendered before the routed app. That keeps named screens
 * independent from the $pageview owner while ensuring React mounts the route
 * pageview effect before passive bootstrap effects in a destination screen.
 * `screen-tracker.test.tsx` and `trackers.test.tsx` hold those invariants.
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
export const Trackers = ({ children }: Props): ReactElement => {
  const { isProduction } = useConfig();

  return (
    <>
      {/* Named in-studio screen_* events. A childless SIBLING of the $pageview
          owner, rendered before the environment branch on purpose: it must mount
          in every environment (Product validates per-room screens in itg/staging),
          and it must not inherit any other tracker's mount condition or wrap the
          app subtree. Nesting it under a tracker whose mount is conditional is
          how the named screens would silently go dark outside production. */}
      <ScreenTracker />
      {/* Route $pageviews. This childless sibling precedes the routed subtree
          so its passive effect commits before route bootstrap effects that may
          synchronously churn or remount that subtree. */}
      <PageviewTracker />
      {isProduction ? <ThirdPartyTags /> : null}
      {children}
    </>
  );
};
